/**
 * Supabase implementation of AuthAdapter.
 * Used for the cloud / Pro tier.
 */

import { createClient } from '@/lib/supabase/client';
import type { AuthAdapter, AuthUser, AuthSession, SignUpOptions } from '../interfaces';

function mapUser(u: any): AuthUser {
  return {
    id: u.id,
    email: u.email ?? null,
    emailConfirmedAt: u.email_confirmed_at ?? null,
    metadata: u.user_metadata ?? {},
    createdAt: u.created_at,
  };
}

function mapSession(s: any): AuthSession {
  return {
    accessToken: s.access_token,
    refreshToken: s.refresh_token ?? null,
    expiresAt: s.expires_at ?? null,
    user: mapUser(s.user),
  };
}

export class SupabaseAuthAdapter implements AuthAdapter {
  private get client() {
    return createClient();
  }

  async signUp(email: string, password: string, options?: SignUpOptions) {
    const { data, error } = await this.client.auth.signUp({
      email,
      password,
      options: {
        data: options?.metadata ?? {},
        emailRedirectTo: options?.redirectTo,
      },
    });
    if (error) throw error;
    return {
      user: data.user ? mapUser(data.user) : null,
      session: data.session ? mapSession(data.session) : null,
    };
  }

  async signIn(email: string, password: string) {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) {
      return { user: null, session: null, error };
    }
    return {
      user: data.user ? mapUser(data.user) : null,
      session: data.session ? mapSession(data.session) : null,
      error: null,
    };
  }

  async signOut() {
    const { error } = await this.client.auth.signOut();
    if (error) throw error;
  }

  async getUser(): Promise<AuthUser | null> {
    const { data: { user }, error } = await this.client.auth.getUser();
    if (error || !user) return null;
    return mapUser(user);
  }

  async getSession(): Promise<AuthSession | null> {
    const { data: { session }, error } = await this.client.auth.getSession();
    if (error || !session) return null;
    return mapSession(session);
  }

  onAuthStateChange(
    callback: (event: 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED', session: AuthSession | null) => void
  ): () => void {
    const { data: { subscription } } = this.client.auth.onAuthStateChange((event, session) => {
      const mapped = session ? mapSession(session) : null;
      callback(event as any, mapped);
    });
    return () => subscription.unsubscribe();
  }
}
